// Crash recovery. If a surface throws, we never lose work: the universe lives
// in IndexedDB, untouched. We show a calm way back and record the failure
// through the central analytics service (never a raw provider call).

import { Component, type ReactNode } from "react";
import { track } from "@/analytics/service";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
  reportState: "idle" | "open" | "sending" | "sent";
  note: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reportState: "idle", note: "" };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  // Emails the owner via /api/report-error. That route always 200s, so a failure
  // here is never surfaced; we just land on "sent".
  private sendReport = async (): Promise<void> => {
    this.setState({ reportState: "sending" });
    try {
      await fetch("/api/report-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: typeof window === "undefined" ? "" : window.location.href,
          errorName: this.state.error?.name || "Error",
          message: (this.state.error?.message || "").slice(0, 500),
          note: this.state.note,
          surface: "app",
        }),
      });
    } catch {
      /* the route always 200s; a network failure here is not worth surfacing */
    }
    this.setState({ reportState: "sent" });
  };

  componentDidCatch(error: Error): void {
    // eslint-disable-next-line no-console
    console.error("Beat Draft surface crashed:", error);
    try {
      track("error.surfaced", { code: error.name || "crash", surface: location.pathname.split("/")[1] || "app" });
    } catch {
      /* analytics must never mask the original error */
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="bd-crash" role="alert">
        <div className="bd-crash__panel">
          <div className="bd-crash__mark" aria-hidden>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
              <circle cx="12" cy="12" r="10" opacity="0.4" />
              <path d="M12 7v6M12 16.5v.5" strokeLinecap="round" />
            </svg>
          </div>
          <h1>Something broke on this screen</h1>
          <p>Your work is safe on your device. Nothing was lost. Reload to pick up right where you were.</p>
          <div className="bd-crash__actions">
            <button className="bd-btn bd-btn--primary" onClick={() => location.reload()}>
              Reload
            </button>
            <a className="bd-btn bd-btn--ghost" href="/universe">
              Back to your universe
            </a>
            {this.state.reportState === "idle" && (
              <button className="bd-btn bd-btn--ghost" onClick={() => this.setState({ reportState: "open" })}>
                Report a problem
              </button>
            )}
          </div>
          {this.state.reportState === "sent" && (
            <p style={{ marginTop: 12, opacity: 0.7, fontSize: 14 }}>Thanks. We got the report and will look into it.</p>
          )}
          {(this.state.reportState === "open" || this.state.reportState === "sending") && (
            <div style={{ marginTop: 12, display: "grid", gap: 8, textAlign: "left" }}>
              <textarea
                value={this.state.note}
                onChange={(e) => this.setState({ note: e.target.value })}
                rows={3}
                placeholder="What were you doing when this happened? (optional)"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14, border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: "inherit", resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="bd-btn bd-btn--primary" disabled={this.state.reportState === "sending"} onClick={this.sendReport}>
                  {this.state.reportState === "sending" ? "Sending…" : "Send report"}
                </button>
                <button className="bd-btn bd-btn--ghost" onClick={() => this.setState({ reportState: "idle" })}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
